import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FilePen, Ban } from 'lucide-react';
import {
  EyeExaminationDisplayStatus,
  type EyeExaminationDetail,
  type EyeExaminationSummary,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { useLocale } from '@/hooks/useLocale';
import { formatDateTime } from '@/utils/format';
import { isolate } from '../bidi';
import { ExaminationStatusBadge } from './ExaminationStatusBadge';

const recordPath = (customerId: string, examinationId: string) =>
  `/customers/${customerId}/eye-examinations/${examinationId}`;

function RecordLink({
  customerId,
  examinationId,
  label,
}: {
  customerId: string;
  examinationId: string;
  label: string;
}) {
  return (
    <Link
      to={recordPath(customerId, examinationId)}
      className="text-brand-700 hover:text-brand-800 font-medium underline underline-offset-2"
    >
      {label}
    </Link>
  );
}

/**
 * How a record that is no longer a draft came to be what it is: who completed it, and whether it was later replaced, voided or discarded, with the reason the record keeps.
 *
 * Replaced, voided and discarded read differently on purpose: a replacement is a correction that lives on in its amendment, a void retires a signed record, and a discard retires a draft that was never signed.
 */
export function LifecycleSummary({
  detail,
  customerId,
}: {
  detail: EyeExaminationDetail;
  customerId: string;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const when = (iso: string) => formatDateTime(iso, locale);
  const status = detail.displayStatus;

  return (
    <div className="space-y-2">
      {detail.completedAt ? (
        <Alert tone={status === EyeExaminationDisplayStatus.COMPLETED ? 'success' : 'info'}>
          {t('medical.completion.by', {
            name: detail.completedByName ?? detail.examinerName ?? '—',
            date: when(detail.completedAt),
          })}
        </Alert>
      ) : null}

      {status === EyeExaminationDisplayStatus.REPLACED && detail.voidedAt ? (
        <Alert tone="warning">
          <span className="block font-medium">
            {t('medical.completion.replacedBy', {
              number: isolate(detail.replacedByExaminationNumber ?? '—'),
              date: when(detail.voidedAt),
            })}
          </span>
          {detail.voidReason ? (
            <span className="block">
              {t('medical.lifecycle.amendmentReason', { reason: detail.voidReason })}
            </span>
          ) : null}
          <span className="block">{t('medical.completion.replacedNotice')}</span>
          {detail.replacedByExaminationId ? (
            <span className="mt-1 block">
              <RecordLink
                customerId={customerId}
                examinationId={detail.replacedByExaminationId}
                label={t('medical.lifecycle.openRecord', {
                  number: isolate(detail.replacedByExaminationNumber ?? '—'),
                })}
              />
            </span>
          ) : null}
        </Alert>
      ) : null}

      {status === EyeExaminationDisplayStatus.VOIDED && detail.voidedAt ? (
        <Alert tone="warning">
          <span className="block font-medium">
            {t('medical.completion.voided', {
              name: detail.voidedByName ?? '—',
              date: when(detail.voidedAt),
            })}
          </span>
          {detail.voidReason ? (
            <span className="block">
              {t('medical.completion.voidReason', { reason: detail.voidReason })}
            </span>
          ) : null}
          <span className="block">{t('medical.completion.voidedNotice')}</span>
        </Alert>
      ) : null}

      {status === EyeExaminationDisplayStatus.DISCARDED && detail.voidedAt ? (
        <Alert tone="warning">
          <span className="block font-medium">
            {t('medical.completion.discardedBy', {
              name: detail.voidedByName ?? '—',
              date: when(detail.voidedAt),
            })}
          </span>
          {detail.voidReason ? (
            <span className="block">
              {t('medical.completion.voidReason', { reason: detail.voidReason })}
            </span>
          ) : null}
          <span className="block">{t('medical.completion.discardedNotice')}</span>
        </Alert>
      ) : null}

      <p className="text-muted text-sm">{t('medical.page.readOnlyNotice')}</p>
    </div>
  );
}

/**
 * The examination an amendment corrects, as read-only context: which record, why, and where it stands now.
 *
 * A draft amendment says plainly that the original is replaced only when the amendment is completed.
 */
export function AmendmentContext({
  detail,
  customerId,
  original,
}: {
  detail: EyeExaminationDetail;
  customerId: string;
  original: EyeExaminationDetail | undefined;
}) {
  const { t } = useTranslation();
  if (!detail.replacesExaminationId) return null;
  const number = isolate(detail.replacesExaminationNumber ?? '—');
  const isOpen = detail.status === 'DRAFT';

  return (
    <Alert tone="info">
      <span className="block font-medium">
        {t(detail.completedAt ? 'medical.lifecycle.replaces' : 'medical.lifecycle.amendmentOf', {
          number,
        })}
      </span>
      {isOpen ? (
        <span className="block">{t('medical.lifecycle.amendmentOfHint', { number })}</span>
      ) : null}
      {detail.amendmentReason ? (
        <span className="block">
          {t('medical.lifecycle.amendmentReason', { reason: detail.amendmentReason })}
        </span>
      ) : null}
      <span className="mt-2 flex flex-wrap items-center gap-2">
        {original ? <ExaminationStatusBadge status={original.displayStatus} /> : null}
        <RecordLink
          customerId={customerId}
          examinationId={detail.replacesExaminationId}
          label={t('medical.lifecycle.openRecord', { number })}
        />
      </span>
    </Alert>
  );
}

interface CorrectionsSectionProps {
  customerId: string;
  canAmend: boolean;
  canVoid: boolean;
  openAmendment: EyeExaminationSummary | undefined;
  onAmend: () => void;
  onVoid: () => void;
}

/**
 * What can still happen to a completed examination: an amendment that corrects it, or a void that retires it.
 *
 * While an amendment is open it is offered instead of a second one, and voiding waits for it: the API refuses both, so the screen says why before anyone tries.
 */
export function CorrectionsSection({
  customerId,
  canAmend,
  canVoid,
  openAmendment,
  onAmend,
  onVoid,
}: CorrectionsSectionProps) {
  const { t } = useTranslation();
  const openNumber = openAmendment ? isolate(openAmendment.examinationNumber) : null;
  const voidBlockedId = 'examination-void-blocked';

  return (
    <div className="space-y-3">
      <p className="text-muted text-sm">{t('medical.lifecycle.completedHint')}</p>

      {openAmendment ? (
        <p className="text-ink flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span>{t('medical.lifecycle.amendmentInProgress', { number: openNumber })}</span>
          <RecordLink
            customerId={customerId}
            examinationId={openAmendment.id}
            label={t('medical.lifecycle.openExistingAmendment')}
          />
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {canAmend && !openAmendment ? (
          <Button variant="secondary" onClick={onAmend}>
            <FilePen className="size-4" aria-hidden />
            {t('medical.lifecycle.amend')}
          </Button>
        ) : null}
        {canVoid ? (
          <Button
            variant="secondary"
            onClick={onVoid}
            disabled={Boolean(openAmendment)}
            aria-describedby={openAmendment ? voidBlockedId : undefined}
          >
            <Ban className="size-4" aria-hidden />
            {t('medical.lifecycle.void')}
          </Button>
        ) : null}
      </div>

      {canVoid && openAmendment ? (
        <p id={voidBlockedId} className="text-muted text-xs">
          {t('medical.lifecycle.voidBlocked', { number: openNumber })}
        </p>
      ) : null}
    </div>
  );
}
